/*
 * vv-design-resolver.js — shared canonical variant resolver for Vivid Ads.
 *
 * ONE source of truth used by BOTH the product page and the Design Online modal.
 * Rule (proven live on the product page, 1 Oct 2026):
 *   canonical variant = the variant whose DESIGN-AFFECTING option values match the
 *   customer's choice, with EVERY OTHER option at its product-page default (the first
 *   listed option value — the non-D# base). This mirrors Shopify's native selector and
 *   never exposes D#/T# SKUs, never picks by price, never picks "first variant".
 *
 * Availability is part of resolution: a geometrically valid but unavailable variant is
 * returned with available:false and is NOT launchable (the caller blocks launch).
 *
 * If the design-affecting fields are not all chosen, or no variant matches, the result
 * is { status: "CONFIG_REQUIRED" } — there is NO first-variant fallback.
 */
(function (root) {
  "use strict";

  function numId(gid) {
    var m = String(gid == null ? "" : gid).match(/(\d+)\s*$/);
    return m ? m[1] : String(gid);
  }

  // Parse width/height (mm) from a size option value, e.g. "800mm W x 2000mm H - SALE".
  function parseDims(sizeValue) {
    if (!sizeValue) return { widthMm: null, heightMm: null };
    var m = String(sizeValue).match(/(\d{2,5})\s*mm[^0-9]*?x[^0-9]*?(\d{2,5})\s*mm/i);
    if (!m) m = String(sizeValue).match(/(\d{2,5})\s*[x×]\s*(\d{2,5})/); // fallback "600 x 900"
    return m ? { widthMm: Number(m[1]), heightMm: Number(m[2]) } : { widthMm: null, heightMm: null };
  }

  // Default value for a non-design option = the first listed value (the non-D# base).
  function defaultValueFor(product, optionName) {
    var opt = (product.options || []).find(function (o) { return o.name.trim() === optionName.trim(); });
    return opt && opt.values && opt.values.length ? opt.values[0] : null;
  }

  function variantOptionValue(variant, optionName) {
    var so = (variant.selectedOptions || []).find(function (o) { return o.name.trim() === optionName.trim(); });
    return so ? so.value : undefined;
  }

  /*
   * resolveCanonicalVariant(product, designFields, chosen)
   *   product      : { handle, options:[{name,values}], variants:[{id,title,availableForSale,selectedOptions:[{name,value}]}] }
   *   designFields : array of design-affecting option names the modal collects, e.g. ["Size"] or ["Size","Printed Sides"]
   *   chosen       : { "<optionName>": "<exact Shopify option value>" , ... } for each designField
   *
   * returns { variantId, variantTitle, available, widthMm, heightMm, designOptions }
   *      or { status:"CONFIG_REQUIRED", reason }
   */
  function resolveCanonicalVariant(product, designFields, chosen) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) {
      return { status: "CONFIG_REQUIRED", reason: "no-product-variants" };
    }
    designFields = designFields || [];
    chosen = chosen || {};

    // 1. Every design-affecting field must be chosen — otherwise CONFIG_REQUIRED (no fallback).
    for (var i = 0; i < designFields.length; i++) {
      var f = designFields[i];
      if (chosen[f] == null || chosen[f] === "") {
        return { status: "CONFIG_REQUIRED", reason: "missing-field:" + f };
      }
    }

    // 2. Build the full target option set: design fields = chosen; all others = default.
    var target = {};
    (product.options || []).forEach(function (o) {
      var name = o.name.trim();
      if (designFields.indexOf(name) !== -1 || designFields.indexOf(o.name) !== -1) {
        target[name] = chosen[name] != null ? chosen[name] : chosen[o.name];
      } else {
        target[name] = defaultValueFor(product, name); // non-D# base default
      }
    });

    // 3. Find the single variant matching every target option exactly.
    var match = product.variants.find(function (v) {
      return Object.keys(target).every(function (name) {
        return variantOptionValue(v, name) === target[name];
      });
    });

    if (!match) return { status: "CONFIG_REQUIRED", reason: "no-matching-variant" };

    var sizeField = designFields.find(function (f) { return /size/i.test(f); }) ||
      (product.options || []).map(function (o) { return o.name; }).find(function (n) { return /size/i.test(n); });
    var dims = parseDims(sizeField ? (chosen[sizeField] || variantOptionValue(match, sizeField)) : match.title);

    return {
      variantId: numId(match.id),
      variantTitle: match.title,
      available: match.availableForSale !== false,
      widthMm: dims.widthMm,
      heightMm: dims.heightMm,
      designOptions: target
    };
  }

  // Distinct customer-facing values for a design field, in listed order (for modal chips).
  // (D#/T# dedupe is automatic for non-size options because they stay at default; size
  //  values are already distinct geometries.)
  function designFieldValues(product, optionName) {
    var opt = (product.options || []).find(function (o) { return o.name.trim() === optionName.trim(); });
    if (!opt) return [];
    // Drop D#/T# discount-engine duplicate values (e.g. "Medium (3000mm H) (D#24K21I4)") —
    // the clean customer-facing geometry is a separate value — and dedupe by cleaned label,
    // so the modal only ever shows real, orderable geometries as chips.
    var seen = {}, out = [];
    opt.values.forEach(function (v) {
      if (/\((?:D|T)#/.test(v)) return;
      var key = String(v).replace(/\s+/g, " ").trim();
      if (seen[key]) return; seen[key] = 1; out.push(v);
    });
    return out;
  }

  var api = { resolveCanonicalVariant: resolveCanonicalVariant, designFieldValues: designFieldValues, parseDims: parseDims };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.VVDesignResolver = api;
})(typeof window !== "undefined" ? window : globalThis);
